import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang, UI } from "../i18n";
import { byCategory, THEMES, TRAVEL, FICHE, IMG } from "../content";
import { useLenis, Reveal } from "../components/motion";
import { PageHero } from "../components/PageHero";
import { MiniSite, MonoTag, Stamp, Barcode, PlaneIcon, FlightDivider } from "../components/motifs";
import { ContactSection } from "../components/Layout";

const Pass = ({ d }) => {
  const { t } = useLang();
  const f = d.flight;
  const th = d.theme;
  return (
    <article data-testid={`immersion-${d.slug}`}
      className="bp relative grid grid-cols-1 lg:grid-cols-12 overflow-hidden rounded-xl border border-white/10"
      style={{ backgroundColor: "var(--ms-surface)" }}>
      <div className="relative lg:col-span-4 h-64 lg:h-auto overflow-hidden">
        <img src={d.img} alt={d.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${th.accent}88, transparent)` }} />
        <Stamp top={t(d.country)} bottom={f.to} className="absolute top-4 left-4" />
      </div>
      <div className="lg:col-span-6 p-7 md:p-10 border-b lg:border-b-0 lg:border-r border-dashed border-white/25">
        <div className="flex items-center gap-4 font-display text-4xl md:text-5xl">
          <span>{f.from}</span>
          <span className="relative flex-1 max-w-[150px] border-t-2 border-dashed border-white/30">
            <PlaneIcon size={20} className="absolute -top-3 left-1/2 -translate-x-1/2 rotate-90" style={{ color: th.accent }} />
          </span>
          <span style={{ color: th.accent }}>{f.to}</span>
        </div>
        <h2 className="font-display text-3xl md:text-5xl uppercase leading-none mt-5">{d.name}</h2>
        {d.comingSoon && (
          <MonoTag className="blink inline-block mt-3 border rounded-full px-3 py-1.5" style={{ color: th.accent, borderColor: `${th.accent}66` }}>
            {t({ fr: "Ouverture prochaine", en: "Opening soon", es: "Próxima apertura" })}
          </MonoTag>
        )}
        <div className="flex flex-wrap gap-x-8 gap-y-2 mt-5 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-white/60">
          <span>{t(FICHE.age)}: {t(d.ages)}</span>
          <span>{t(TRAVEL.duration)}: {t(d.duration)}</span>
          <span>{t(TRAVEL.gate)}: {f.gate}</span>
        </div>
        <p className="font-body text-white/75 mt-6">{t(d.desc)}</p>
        <Link to={`/immersions/${d.slug}`} data-testid={`immersion-cta-${d.slug}`}
          className="inline-flex items-center gap-2 mt-7 rounded-full px-6 py-3 font-heading font-semibold uppercase text-xs tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
          style={{ backgroundColor: th.accent }}>
          {t(FICHE.seeFiche)} <ArrowUpRight size={16} />
        </Link>
      </div>
      <div className="lg:col-span-2 p-7 flex flex-row lg:flex-col items-center justify-between gap-5">
        <MonoTag className="text-white/60 lg:[writing-mode:vertical-rl]">{t(TRAVEL.flight)} {d.code}</MonoTag>
        <Barcode className="hidden sm:block h-10 w-28 lg:w-14 lg:h-32 text-white/60" />
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] blink text-center" style={{ color: th.accent }}>
          {t(TRAVEL.boarding)}
        </span>
      </div>
    </article>
  );
};

export default function Immersions() {
  useLenis();
  const { t } = useLang();
  const list = byCategory("immersions");
  return (
    <MiniSite theme={THEMES.immersion}>
      <PageHero
        code="TERMINAL 02"
        overline={t(UI.nav.immersions)}
        title={t({ fr: "7 jours dans la peau d'un pro", en: "7 days in a pro's shoes", es: "7 días como un pro" })}
        subtitle={t({ fr: "Un stage d'une semaine en immersion complète dans notre équipe partenaire : entraînements, matchs, musculation et routine d'athlète. Départs individuels au choix, de septembre à début mars.", en: "A one-week full immersion inside our partner team: practices, games, weight room and athlete routine. Individual departures from September to early March.", es: "Una semana de inmersión completa en nuestro equipo asociado: entrenamientos, partidos, gimnasio y rutina de atleta. Salidas individuales de septiembre a inicios de marzo." })}
        image={IMG.planeWindow}
      />
      <section className="pb-24 md:pb-32 max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 mb-10 font-mono text-[10px] md:text-[11px] uppercase tracking-[0.3em] text-white/50" data-testid="immersion-concept-strip">
          <span style={{ color: "#F6B02C" }}>550 € / {t({ fr: "semaine", en: "week", es: "semana" })}</span>
          <span>{t({ fr: "Hébergement + encadrement basket inclus", en: "Housing + basketball supervision included", es: "Alojamiento + supervisión incluidos" })}</span>
          <span>{t({ fr: "Hors billet d'avion", en: "Flight not included", es: "Vuelo no incluido" })}</span>
          <span className="blink" style={{ color: "#F6B02C" }}>● {t(TRAVEL.nowBoarding)}</span>
        </div>
        <div className="space-y-10">
          {list.map((d, i) => (
            <React.Fragment key={d.slug}>
              {i > 0 && <FlightDivider />}
              <Reveal><Pass d={d} /></Reveal>
            </React.Fragment>
          ))}
        </div>
      </section>
      <ContactSection programs={list.map((d) => d.name)} />
    </MiniSite>
  );
}
